import { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { organisationActions } from '../../redux/slices/organisation.slice';
import {
  IOrganisationAccess,
  IOrganisationMemberAccess,
  TOrganisationMemberRoleFilter,
  TOrganisationMemberSort,
} from '../../types/@organisation';

interface IProps {
  organisationId: string;
  access: IOrganisationAccess;
  currentUserId: string;
  isSaving: boolean;
}

type TConfirmAction = {
  type: 'removeMember' | 'removeAdmin' | 'removeHelper' | 'leaveOrganisation' | null;
  userId?: string;
  label?: string;
};

const uniqueIds = (values: string[]): string[] => {
  return values.filter((value, index, array) => array.indexOf(value) === index);
};

const OrganisationAccessPanel = ({
  organisationId,
  access,
  currentUserId,
  isSaving,
}: IProps): ReactElement => {
  const dispatch = useDispatch();

  const [selectedAdminUserId, setSelectedAdminUserId] = useState<string>('');
  const [selectedHelperUserId, setSelectedHelperUserId] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<TConfirmAction>({ type: null });

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<TOrganisationMemberRoleFilter>('all');
  const [sortBy, setSortBy] = useState<TOrganisationMemberSort>('activity');

  const adminIdSet = useMemo(() => new Set(access.admins), [access.admins]);
  const helperIdSet = useMemo(() => new Set(access.helpers), [access.helpers]);

  const memberMap = useMemo(() => {
    return access.members.reduce((acc: Record<string, IOrganisationMemberAccess>, member) => {
      acc[member.userId] = member;
      return acc;
    }, {});
  }, [access.members]);

  const getMemberRole = useCallback(
    (userId: string): 'admin' | 'helper' | 'member' => {
      if (adminIdSet.has(userId)) return 'admin';
      if (helperIdSet.has(userId)) return 'helper';
      return 'member';
    },
    [adminIdSet, helperIdSet]
  );

  const rolePriority = useCallback((role: 'admin' | 'helper' | 'member'): number => {
    if (role === 'admin') return 3;
    if (role === 'helper') return 2;
    return 1;
  }, []);

  const formatLastActivity = useCallback((value: string | null): string => {
    if (!value) return '—';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleString();
  }, []);

  const adminMembers = useMemo(() => {
    return access.admins.map((userId) => memberMap[userId]).filter(Boolean);
  }, [access.admins, memberMap]);

  const helperMembers = useMemo(() => {
    return access.helpers.map((userId) => memberMap[userId]).filter(Boolean);
  }, [access.helpers, memberMap]);

  const eligibleAdminCandidates = useMemo(() => {
    return access.members.filter((member) => !adminIdSet.has(member.userId));
  }, [access.members, adminIdSet]);

  const eligibleHelperCandidates = useMemo(() => {
    return access.members.filter(
      (member) => !helperIdSet.has(member.userId) && !adminIdSet.has(member.userId)
    );
  }, [access.members, helperIdSet, adminIdSet]);

  const myMemberRecord = useMemo(() => {
    return access.members.find((member) => member.userId === currentUserId) || null;
  }, [access.members, currentUserId]);

  const isAdmin = access.myRole === 'admin';

  const filteredAndSortedMembers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filtered = access.members.filter((member) => {
      const memberRole = getMemberRole(member.userId);

      const matchesRole = roleFilter === 'all' ? true : memberRole === roleFilter;

      const matchesSearch =
        !normalizedSearch ||
        (member.name || '').toLowerCase().includes(normalizedSearch) ||
        (member.email || '').toLowerCase().includes(normalizedSearch) ||
        member.userId.toLowerCase().includes(normalizedSearch);

      return matchesRole && matchesSearch;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === 'activity') {
        const aTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
        const bTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;

        if (bTime !== aTime) return bTime - aTime;
      }

      if (sortBy === 'items') {
        if (b.currentItemsOwned !== a.currentItemsOwned) {
          return b.currentItemsOwned - a.currentItemsOwned;
        }
      }

      if (sortBy === 'role') {
        const roleDiff = rolePriority(getMemberRole(b.userId)) - rolePriority(getMemberRole(a.userId));
        if (roleDiff !== 0) return roleDiff;
      }

      const aName = (a.name || a.email || a.userId).toLowerCase();
      const bName = (b.name || b.email || b.userId).toLowerCase();

      return aName.localeCompare(bName);
    });
  }, [access.members, getMemberRole, roleFilter, rolePriority, searchTerm, sortBy]);

  const handleAccessSave = (admins: string[], helpers: string[]) => {
    dispatch(
      organisationActions.updateOrganisationAccess({
        id: organisationId,
        payload: {
          admins,
          helpers,
        },
      })
    );
  };

  const handleAddAdmin = () => {
    if (!selectedAdminUserId || !isAdmin) return;

    const nextAdmins = uniqueIds(access.admins.concat(selectedAdminUserId));
    const nextHelpers = access.helpers.filter((id) => id !== selectedAdminUserId);

    handleAccessSave(nextAdmins, nextHelpers);
    setSelectedAdminUserId('');
  };

  const handleRemoveAdmin = (userId: string) => {
    const nextAdmins = access.admins.filter((id) => id !== userId);
    handleAccessSave(nextAdmins, access.helpers);
  };

  const handleAddHelper = () => {
    if (!selectedHelperUserId || !isAdmin) return;

    const nextHelpers = uniqueIds(access.helpers.concat(selectedHelperUserId));
    const nextAdmins = access.admins.filter((id) => id !== selectedHelperUserId);

    handleAccessSave(nextAdmins, nextHelpers);
    setSelectedHelperUserId('');
  };

  const handleRemoveHelper = (userId: string) => {
    const nextHelpers = access.helpers.filter((id) => id !== userId);
    handleAccessSave(access.admins, nextHelpers);
  };

  const handleRemoveMember = (userId: string) => {
    dispatch(
      organisationActions.removeOrganisationMember({
        id: organisationId,
        userId,
      })
    );
  };

  const handleLeaveOrganisation = () => {
    dispatch(
      organisationActions.leaveOrganisation({
        id: organisationId,
      })
    );
  };

  const openConfirm = (nextAction: TConfirmAction) => {
    setConfirmAction(nextAction);
  };

  const closeConfirm = () => {
    setConfirmAction({ type: null });
  };

  const runConfirmedAction = () => {
    if (!confirmAction.type) return;

    if (confirmAction.type === 'removeMember' && confirmAction.userId) {
      handleRemoveMember(confirmAction.userId);
    }

    if (confirmAction.type === 'removeAdmin' && confirmAction.userId) {
      handleRemoveAdmin(confirmAction.userId);
    }

    if (confirmAction.type === 'removeHelper' && confirmAction.userId) {
      handleRemoveHelper(confirmAction.userId);
    }

    if (confirmAction.type === 'leaveOrganisation') {
      handleLeaveOrganisation();
    }

    closeConfirm();
  };

  const getConfirmText = () => {
    switch (confirmAction.type) {
      case 'removeMember':
        return `Remove ${confirmAction.label || 'this member'} from the organisation?`;
      case 'removeAdmin':
        return `Remove admin access from ${confirmAction.label || 'this user'}?`;
      case 'removeHelper':
        return `Remove helper access from ${confirmAction.label || 'this user'}?`;
      case 'leaveOrganisation':
        return 'Are you sure you want to leave this organisation?';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
          Access management
        </Typography>
        <Chip label={'My role: ' + (access.myRole || 'none')} color={isAdmin ? 'primary' : 'default'} />
      </Box>

      {!isAdmin && (
        <Alert severity="info">
          You can view membership here. Only organisation admins can update roles or remove members.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.3fr 1fr' },
          gap: 3,
        }}
      >
        <Paper sx={{ p: 3, borderRadius: '16px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d', mb: 2 }}>
            Members
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr 1fr' },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            <TextField
              size="small"
              fullWidth
              label="Search members"
              placeholder="Search by name, email or id"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />

            <FormControl size="small" fullWidth>
              <Select
                displayEmpty
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value as TOrganisationMemberRoleFilter)}
              >
                <MenuItem value="all">All roles</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
                <MenuItem value="helper">Helpers</MenuItem>
                <MenuItem value="member">Members</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <Select
                displayEmpty
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value as TOrganisationMemberSort)}
              >
                <MenuItem value="activity">Sort by activity</MenuItem>
                <MenuItem value="items">Sort by item count</MenuItem>
                <MenuItem value="role">Sort by role</MenuItem>
                <MenuItem value="name">Sort by name</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredAndSortedMembers.length ? (
              filteredAndSortedMembers.map((member) => {
                const isCurrentUser = member.userId === currentUserId;
                const canRemove = isAdmin && member.currentItemsOwned === 0 && !isCurrentUser;
                const canLeave = isCurrentUser && member.currentItemsOwned === 0;
                const memberRole = getMemberRole(member.userId);

                return (
                  <Box
                    key={member.userId}
                    sx={{
                      p: 2,
                      borderRadius: '14px',
                      border: '1px solid #e3e8ef',
                      background: '#fafbfd',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      alignItems: { xs: 'flex-start', md: 'center' },
                      flexDirection: { xs: 'column', md: 'row' },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#17324d' }}>
                        {member.name || member.email || member.userId}
                      </Typography>

                      <Typography sx={{ fontSize: '14px', color: '#5f7285' }}>
                        {member.email || 'No email'}
                      </Typography>

                      <Typography sx={{ fontSize: '13px', color: '#7a8a99' }}>
                        Last active: {formatLastActivity(member.lastActivityAt)}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={member.ownsCurrentThing ? 'Has current org item' : 'No current org item'}
                          color={member.ownsCurrentThing ? 'success' : 'default'}
                        />
                        <Chip size="small" label={'Current items: ' + member.currentItemsOwned} />

                        {memberRole === 'admin' && <Chip size="small" color="primary" label="Admin" />}
                        {memberRole === 'helper' && <Chip size="small" color="secondary" label="Helper" />}
                        {memberRole === 'member' && <Chip size="small" label="Member" />}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {canRemove && (
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={isSaving}
                          onClick={() =>
                            openConfirm({
                              type: 'removeMember',
                              userId: member.userId,
                              label: member.name || member.email || member.userId,
                            })
                          }
                          sx={{ textTransform: 'none', borderRadius: '12px' }}
                        >
                          Remove member
                        </Button>
                      )}

                      {canLeave && (
                        <Button
                          variant="contained"
                          disabled={isSaving}
                          onClick={() => openConfirm({ type: 'leaveOrganisation' })}
                          sx={{ textTransform: 'none', borderRadius: '12px' }}
                        >
                          Leave organisation
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Alert severity="info">No members match the current search/filter.</Alert>
            )}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 3, borderRadius: '16px' }} elevation={0} variant="outlined">
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d', mb: 2 }}>
              Admins
            </Typography>

            {isAdmin && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={selectedAdminUserId}
                    onChange={(event) => setSelectedAdminUserId(event.target.value as string)}
                  >
                    <MenuItem value="">
                      <em>Select member</em>
                    </MenuItem>
                    {eligibleAdminCandidates.map((member) => (
                      <MenuItem key={member.userId} value={member.userId}>
                        {member.name || member.email || member.userId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  disabled={isSaving || !selectedAdminUserId}
                  onClick={handleAddAdmin}
                  sx={{ textTransform: 'none', borderRadius: '12px', whiteSpace: 'nowrap' }}
                >
                  Add admin
                </Button>
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {adminMembers.length ? (
                adminMembers.map((member) => (
                  <Box
                    key={member.userId}
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      background: '#fafbfd',
                      border: '1px solid #e3e8ef',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#17324d' }}>
                        {member.name || member.email || member.userId}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#5f7285' }}>{member.email || '-'}</Typography>
                    </Box>

                    {isAdmin && (
                      <Button
                        variant="text"
                        color="error"
                        disabled={isSaving}
                        onClick={() =>
                          openConfirm({
                            type: 'removeAdmin',
                            userId: member.userId,
                            label: member.name || member.email || member.userId,
                          })
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))
              ) : (
                <Alert severity="warning">No admins found.</Alert>
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: '16px' }} elevation={0} variant="outlined">
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d', mb: 2 }}>
              Helpers
            </Typography>

            {isAdmin && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={selectedHelperUserId}
                    onChange={(event) => setSelectedHelperUserId(event.target.value as string)}
                  >
                    <MenuItem value="">
                      <em>Select member</em>
                    </MenuItem>
                    {eligibleHelperCandidates.map((member) => (
                      <MenuItem key={member.userId} value={member.userId}>
                        {member.name || member.email || member.userId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  disabled={isSaving || !selectedHelperUserId}
                  onClick={handleAddHelper}
                  sx={{ textTransform: 'none', borderRadius: '12px', whiteSpace: 'nowrap' }}
                >
                  Add helper
                </Button>
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {helperMembers.length ? (
                helperMembers.map((member) => (
                  <Box
                    key={member.userId}
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      background: '#fafbfd',
                      border: '1px solid #e3e8ef',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#17324d' }}>
                        {member.name || member.email || member.userId}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#5f7285' }}>{member.email || '-'}</Typography>
                    </Box>

                    {isAdmin && (
                      <Button
                        variant="text"
                        color="error"
                        disabled={isSaving}
                        onClick={() =>
                          openConfirm({
                            type: 'removeHelper',
                            userId: member.userId,
                            label: member.name || member.email || member.userId,
                          })
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))
              ) : (
                <Alert severity="info">No helpers assigned.</Alert>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {myMemberRecord && myMemberRecord.currentItemsOwned > 0 && (
        <Alert severity="info">
          You currently own organisation items, so you cannot leave this organisation yet.
        </Alert>
      )}

      <Dialog open={!!confirmAction.type} onClose={closeConfirm}>
        <DialogTitle>Confirm action</DialogTitle>
        <DialogContent>
          <DialogContentText>{getConfirmText()}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={runConfirmedAction}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganisationAccessPanel;